/**
 * =============================================================================
 * WlFinancialDetailModal
 *
 * Modal generico de detalhamento dos KPIs financeiros do painel WL.
 * Herda o shell visual das listagens do WL (ListPageLayout compact + Table
 * text-xs + PaginationFooter) dentro de um Dialog fullscreen no mobile.
 *
 * Tipos suportados:
 *   - mrr           -> lista tenants pagantes (reaproveita wl_list_tenant_subscriptions)
 *   - paying        -> idem mrr
 *   - plan          -> tenants filtrados por plano especifico
 *   - addon         -> add-ons ativos (opcionalmente filtrados por addon_id)
 *   - at_risk       -> tenants past_due/unpaid
 *
 * @version 1.6.1
 * RELEASE:
 *   2026-07-21 v1.0.0 - Versao inicial.
 *   2026-07-21 v1.0.1 - Scroll interno + drill-down para WlTenantDetailsModal.
 *   2026-07-21 v1.0.2 - Filtro server-side por add-on (p_addon_id).
 *   2026-07-21 v1.0.3 - Corrige cadeia de altura do flex (DialogContent h-[90vh]).
 *   2026-07-21 v1.1.0 - Nova prop `statuses` (multi-select) alinhada ao KPI.
 *   2026-07-21 v1.2.0 - Colunas Valor Plano / Total Add-ons / Total MRR nos
 *                       drill-downs de MRR/ARR (types `mrr` e `paying`),
 *                       consumindo plan_mrr e addon_mrr da RPC v1.5.0. Demais
 *                       tipos (plan, addon, at_risk) permanecem inalterados.
 *   2026-07-21 v1.3.0 - Coluna Valor (drill-downs plan/addon/at_risk) passa a
 *                       priorizar plan_mrr híbrido (fa.valor_total normalizado)
 *                       antes do fallback para o catálogo, corrigindo R$ 0,00
 *                       em planos V1 (Starter v1, Enterprise v1, etc.).
 *   2026-07-22 v1.6.0 - Novas props opcionais para type='addon': activatedFrom/To
 *                       e cancelledFrom/To, propagadas à RPC wl_list_tenant_addons
 *                       v2.0.0 (p_activated_from/to, p_cancelled_from/to). Habilita
 *                       drill-down dos KPIs da aba Add-ons alinhado por período.
 *                       Quando há janela, p_status deixa de forçar 'active'.
 *   2026-07-22 v1.6.1 - Corrige PGRST202: mapeia activatedFrom/To -> p_created_from/to
 *                       e cancelledFrom/To -> p_canceled_from/to (nomes reais da RPC
 *                       em produção). Sem alteração no banco.
 * =============================================================================
 */
import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  ListPageLayout,
  ListPageFiltersBar,
  LIST_FILTERS_INPUT_CLASS,
  LIST_FILTERS_SELECT_TRIGGER_CLASS,
  ListPageContent,
  ListPageFooter,
} from '@/components/layout/list-page';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { WlTenantDetailsModal } from '@/components/wl/WlTenantDetailsModal';

/** Tipos de detalhamento suportados pelo modal. */
export type WlFinancialDetailType = 'mrr' | 'paying' | 'plan' | 'addon' | 'at_risk';

interface WlFinancialDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: WlFinancialDetailType;
  title: string;
  /** Filtro opcional por plano (usado em type='plan'). */
  planId?: string | null;
  /** Filtro opcional por add-on (usado em type='addon'). */
  addonId?: string | null;
  /**
   * Lista opcional de status vinda do overview. Quando informada, o modal usa
   * como filtro base (multi-select) para bater com o KPI clicado. Pode ser
   * refinada localmente pelo dropdown "Status" do modal.
   */
  statuses?: string[];
  /**
   * v1.4.0: Filtros opcionais de período repassados à RPC v1.7.0.
   * Alinham o drill-down aos KPIs mensais (Novo MRR, Crescimento, Perdidos).
   * ISO strings (UTC). Quando ambos os pares estiverem informados e
   * `periodOr` for true, a RPC aplica OR entre "created ∈ range" e
   * "canceled ∈ range" (semântica do card Crescimento de assinantes).
   */
  createdFrom?: string | null;
  createdTo?: string | null;
  canceledFrom?: string | null;
  canceledTo?: string | null;
  periodOr?: boolean;
  /**
   * v1.5.0: janela sobre tenants.deactivated_at — usada pelo card
   * "Assinantes perdidos" para casar 100% com a contagem do KPI
   * (wl_get_financial_kpis v1.8.0 -> trend_tenants.churned_tenants).
   */
  deactivatedFrom?: string | null;
  deactivatedTo?: string | null;
  /**
   * v1.6.0: janelas de período para drill-down de add-ons (type='addon').
   * Propagadas à RPC `wl_list_tenant_addons` como `p_activated_from/to` e
   * `p_cancelled_from/to`, alinhando o modal aos KPIs "Novo Add-on (mês)" e
   * "Add-ons cancelados (mês)" da aba Add-ons.
   */
  activatedFrom?: string | null;
  activatedTo?: string | null;
  cancelledFrom?: string | null;
  cancelledTo?: string | null;
}


const PAGE_SIZE_OPTIONS = [10, 30, 50, 100];

const STATUS_OPTIONS = ['active', 'trialing', 'past_due', 'canceled', 'incomplete', 'unpaid'];

const STATUS_DOT: Record<string, string> = {
  active: 'bg-green-500',
  trialing: 'bg-blue-500',
  past_due: 'bg-yellow-500',
  canceled: 'bg-red-500',
  incomplete: 'bg-zinc-400',
  unpaid: 'bg-red-500',
};

function DotLabel({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-foreground whitespace-nowrap">
      <span className={cn('h-2 w-2 rounded-full shrink-0', color)} />
      {label}
    </span>
  );
}

const fmtBRL = (v: number | null | undefined) =>
  v == null ? '—' : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v));

const fmtDate = (v: string | null) => (v ? new Date(v).toLocaleDateString('pt-BR') : '—');
// v1.7.0: helper para exibir data + hora/minuto (coluna "Dt. Criação").
const fmtDateTime = (v: string | null | undefined) =>
  v
    ? new Date(v).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

/** Modal generico de detalhamento financeiro. */
export function WlFinancialDetailModal({
  open,
  onOpenChange,
  type,
  title,
  planId,
  addonId,
  statuses,
  createdFrom,
  createdTo,
  canceledFrom,
  canceledTo,
  periodOr,
  deactivatedFrom,
  deactivatedTo,
  activatedFrom,
  activatedTo,
  cancelledFrom,
  cancelledTo,
}: WlFinancialDetailModalProps) {
  // -------------------------------------------------------------------------
  // Estado local: busca, status, pagina.
  // -------------------------------------------------------------------------
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>('__all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  // Estado do drill-down: tenant selecionado abre WlTenantDetailsModal por cima.
  const [detailsTenantId, setDetailsTenantId] = useState<string | null>(null);

  // Reset ao trocar tipo/plano/addon.
  useEffect(() => {
    setPage(1);
    setSearch('');
    setStatus('__all');
  }, [type, planId, addonId, open]);

  // -------------------------------------------------------------------------
  // Determina qual RPC chamar.
  // -------------------------------------------------------------------------
  const isAddon = type === 'addon';

  /**
   * Status efetivo enviado ao backend:
   *  - `type='at_risk'` -> forca 'past_due' (semantica propria do KPI);
   *  - se o usuario refinar via dropdown local, usa esse valor;
   *  - caso contrario, retorna null (o backend usa `statuses` multi-select
   *    passado pela prop, alinhando modal ao KPI clicado).
   */
  const effectiveStatus = useMemo(() => {
    if (type === 'at_risk') return 'past_due';
    if (status !== '__all') return status;
    return null;
  }, [type, status]);

  /**
   * Multi-status vindo do overview. Enviado ao backend somente quando o
   * usuario NAO refinou o filtro local (para nao competir com effectiveStatus).
   */
  const effectiveStatuses = useMemo<string[] | null>(() => {
    if (type === 'at_risk') return null;
    if (status !== '__all') return null;
    if (statuses && statuses.length > 0) return statuses;
    return null;
  }, [type, status, statuses]);

  const query = useQuery({
    queryKey: [
      'wl-financial-detail',
      type,
      planId ?? null,
      addonId ?? null,
      search,
      effectiveStatus,
      effectiveStatuses?.join(',') ?? null,
      createdFrom ?? null,
      createdTo ?? null,
      canceledFrom ?? null,
      canceledTo ?? null,
      periodOr ?? false,
      deactivatedFrom ?? null,
      deactivatedTo ?? null,
      activatedFrom ?? null,
      activatedTo ?? null,
      cancelledFrom ?? null,
      cancelledTo ?? null,
      page,
      pageSize,
    ],
    enabled: open,
    staleTime: 30_000,
    queryFn: async () => {
      if (isAddon) {
        // v1.6.0: quando janelas de período são fornecidas (novo/cancelado no
        // mês), o status 'active' seria conflitante com cancelamentos. Só
        // fixa 'active' se nenhuma janela vier — snapshot padrão.
        const hasWindow =
          !!activatedFrom || !!activatedTo || !!cancelledFrom || !!cancelledTo;
        const { data, error } = await supabase.rpc('wl_list_tenant_addons', {
          p_tenant_id: null,
          p_status: hasWindow ? null : 'active',
          p_limit: pageSize,
          p_offset: (page - 1) * pageSize,
          p_addon_id: addonId ?? null,
          // v1.6.1: RPC em produção usa p_created_* (ativação) e p_canceled_*
          // (cancelamento). Mapeia props activated*/cancelled* para os nomes
          // reais da assinatura existente, sem alterar a RPC.
          p_created_from: activatedFrom ?? null,
          p_created_to: activatedTo ?? null,
          p_canceled_from: cancelledFrom ?? null,
          p_canceled_to: cancelledTo ?? null,
        } as never);
        if (error) throw error;
        return { rows: (data ?? []) as any[], total: (data?.[0]?.total_count as number) ?? 0 };
      }
      const { data, error } = await supabase.rpc('wl_list_tenant_subscriptions', {
        p_search: search.trim() || null,
        p_subscription_status: effectiveStatus,
        p_plan: planId ?? null,
        p_limit: pageSize,
        p_offset: (page - 1) * pageSize,
        p_statuses: effectiveStatuses,
        // v1.4.0: filtros de período — alinha drill-down aos KPIs mensais.
        p_created_from: createdFrom ?? null,
        p_created_to: createdTo ?? null,
        p_canceled_from: canceledFrom ?? null,
        p_canceled_to: canceledTo ?? null,
        p_period_or: periodOr ?? false,
        // v1.5.0: janela de deactivated_at (fonte de verdade do KPI churn).
        p_deactivated_from: deactivatedFrom ?? null,
        p_deactivated_to: deactivatedTo ?? null,
      } as never);
      if (error) throw error;
      return { rows: (data ?? []) as any[], total: (data?.[0]?.total_count as number) ?? 0 };
    },
  });

  const rows = query.data?.rows ?? [];
  const total = query.data?.total ?? 0;

  // -------------------------------------------------------------------------
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[95vw] h-[90vh] p-0 gap-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-6 pt-5 pb-3 border-b shrink-0">
          <DialogTitle className="text-base">{title}</DialogTitle>
        </DialogHeader>

        {/* min-h-0 propaga encolhimento para o ListPageContent rolar (fix flexbox). */}
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">

          <ListPageLayout className="flex-1 min-h-0">
            {!isAddon && (
              <ListPageFiltersBar isFetching={query.isFetching}>
                <div className="relative flex-1 min-w-[220px] sm:flex-none sm:w-[280px]">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
                  <Input
                    className={cn(LIST_FILTERS_INPUT_CLASS, 'pl-7 sm:pl-8')}
                    placeholder="Buscar tenant…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') setPage(1);
                    }}
                  />
                </div>
                {type !== 'at_risk' && (
                  <Select
                    value={status}
                    onValueChange={(v) => {
                      setStatus(v);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, 'sm:w-[160px]')}>
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all">Todos status</SelectItem>
                      {STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </ListPageFiltersBar>
            )}

            <ListPageContent className="flex-1 min-h-0 p-0 overflow-hidden">
              {query.isLoading ? (
                <p className="p-4 text-sm text-muted-foreground">Carregando…</p>
              ) : rows.length === 0 ? (
                <p className="p-8 text-center text-sm text-muted-foreground">Nenhum registro</p>
              ) : isAddon ? (
                // Wrapper dedicado ao scroll (vertical + horizontal) da tabela.
                <div className="h-full w-full overflow-auto">
                  <Table className="min-w-[1000px]">
                    <TableHeader className="sticky top-0 bg-background z-10">
                      <TableRow>
                        <TableHead className="text-xs font-medium">Tenant</TableHead>
                        {/* v1.7.1: Dt. Criação seguida de Vigência (coluna reposicionada). */}
                        <TableHead className="text-xs font-medium">Dt. Criação</TableHead>
                        <TableHead className="text-xs font-medium">Vigência</TableHead>
                        <TableHead className="text-xs font-medium">Add-on</TableHead>
                        <TableHead className="text-xs font-medium">Qtd</TableHead>
                        <TableHead className="text-xs font-medium">Valor/mês</TableHead>
                        <TableHead className="text-xs font-medium">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows
                        .map((a: any) => (
                          <TableRow
                            key={a.id}
                            onClick={() => a.tenant_id && setDetailsTenantId(a.tenant_id)}
                            className="cursor-pointer hover:bg-muted/50"
                          >
                            <TableCell className="py-2 text-xs">
                              <div className="font-medium">{a.tenant_name}</div>
                              <div className="text-[11px] text-muted-foreground">{a.tenant_slug}</div>
                            </TableCell>
                            {/* v1.7.1: created_at seguido de vigência (ordem reposicionada). */}
                            <TableCell className="py-2 text-xs whitespace-nowrap">
                              {fmtDateTime(a.created_at)}
                            </TableCell>
                            <TableCell className="py-2 text-xs">
                              {fmtDate(a.current_period_start)} → {fmtDate(a.current_period_end)}
                            </TableCell>
                            <TableCell className="py-2 text-xs">
                              <div className="font-medium">{a.addon_name}</div>
                              <div className="text-[11px] text-muted-foreground">{a.addon_slug}</div>
                            </TableCell>
                            <TableCell className="py-2 text-xs">{a.quantity}</TableCell>
                            <TableCell className="py-2 text-xs">{fmtBRL(a.price_monthly)}</TableCell>
                            <TableCell className="py-2 text-xs">
                              <DotLabel color={STATUS_DOT[a.status] ?? 'bg-zinc-400'} label={a.status} />
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                // Ramo MRR/paying: exibe Valor Plano + Total Add-ons + Total MRR.
                // Demais types (plan, at_risk) mantem coluna unica "Valor".
                (() => {
                  const isMrrLike = type === 'mrr' || type === 'paying';
                  return (
                    <div className="h-full w-full overflow-auto">
                      <Table className={cn(isMrrLike ? 'min-w-[1300px]' : 'min-w-[1000px]')}>
                        <TableHeader className="sticky top-0 bg-background z-10">
                          <TableRow>
                            <TableHead className="text-xs font-medium">Tenant</TableHead>
                            <TableHead className="text-xs font-medium">Plano</TableHead>
                            <TableHead className="text-xs font-medium">Período</TableHead>
                            {isMrrLike ? (
                              <>
                                <TableHead className="text-xs font-medium">Valor Plano</TableHead>
                                <TableHead className="text-xs font-medium">Total Add-ons</TableHead>
                                <TableHead className="text-xs font-medium">Total MRR</TableHead>
                              </>
                            ) : (
                              <TableHead className="text-xs font-medium">Valor</TableHead>
                            )}
                            <TableHead className="text-xs font-medium">Status</TableHead>
                            {/* v1.7.1: Dt. Criação reposicionada para logo antes de Vigência. */}
                            <TableHead className="text-xs font-medium">Dt. Criação</TableHead>
                            <TableHead className="text-xs font-medium">Vigência</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {rows.map((s: any) => {
                            // plan_mrr e addon_mrr vem da RPC v1.5.0 (normalizados p/ mensal).
                            const planMrr = Number(s.plan_mrr ?? 0);
                            const addonMrr = Number(s.addon_mrr ?? 0);
                            const totalMrr = planMrr + addonMrr;
                            return (
                              <TableRow
                                key={s.tenant_id}
                                onClick={() => s.tenant_id && setDetailsTenantId(s.tenant_id)}
                                className="cursor-pointer hover:bg-muted/50"
                              >
                                <TableCell className="py-2 text-xs">
                                  <div className="font-medium">{s.tenant_name}</div>
                                  <div className="text-[11px] text-muted-foreground">{s.tenant_slug}</div>
                                </TableCell>
                                <TableCell className="py-2 text-xs">{s.plan_name ?? s.plan_id ?? '—'}</TableCell>
                                <TableCell className="py-2 text-xs">{s.billing_period ?? '—'}</TableCell>
                                {isMrrLike ? (
                                  <>
                                    <TableCell className="py-2 text-xs">{fmtBRL(planMrr)}</TableCell>
                                    <TableCell className="py-2 text-xs">{fmtBRL(addonMrr)}</TableCell>
                                    <TableCell className="py-2 text-xs font-semibold">
                                      {fmtBRL(totalMrr)}
                                    </TableCell>
                                  </>
                                ) : (
                                  <TableCell className="py-2 text-xs">
                                    {(() => {
                                      // v1.3.0: prioriza plan_mrr híbrido (RPC v1.5.1+),
                                      // que já resolve fa.valor_total (V1) com fallback
                                      // para o catálogo. Só cai no catálogo cru quando
                                      // a RPC não devolveu MRR.
                                      const planMrr = Number(s.plan_mrr ?? 0);
                                      if (planMrr > 0) {
                                        return s.billing_period === 'yearly'
                                          ? fmtBRL(planMrr * 12)
                                          : fmtBRL(planMrr);
                                      }
                                      return s.billing_period === 'yearly'
                                        ? fmtBRL(s.price_yearly)
                                        : fmtBRL(s.price_monthly);
                                    })()}
                                  </TableCell>
                                )}
                                <TableCell className="py-2 text-xs">
                                  <DotLabel
                                    color={STATUS_DOT[s.subscription_status ?? ''] ?? 'bg-zinc-400'}
                                    label={s.subscription_status ?? '—'}
                                  />
                                </TableCell>
                                {/* v1.7.1: Dt. Criação (COALESCE(tbb.created_at, t.created_at)) antes de Vigência. */}
                                <TableCell className="py-2 text-xs whitespace-nowrap">
                                  {fmtDateTime(s.created_at)}
                                </TableCell>
                                <TableCell className="py-2 text-xs">
                                  {fmtDate(s.current_period_start)} → {fmtDate(s.current_period_end)}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  );
                })()
              )}
            </ListPageContent>



            <ListPageFooter className="shrink-0 border-t bg-background">
              <PaginationFooter
                totalItems={total}
                currentPage={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(sz) => {
                  setPageSize(sz);
                  setPage(1);
                }}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
              />
            </ListPageFooter>
          </ListPageLayout>
        </div>
      </DialogContent>

      {/* Drill-down: reaproveita o mesmo modal usado na tela /wl/tenants. */}
      <WlTenantDetailsModal
        tenantId={detailsTenantId}
        open={!!detailsTenantId}
        onClose={() => setDetailsTenantId(null)}
      />
    </Dialog>
  );
}
